<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>
              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="useUserData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_EMAIL')"
                  label-for="email"
                >
                  <b-form-input
                    id="email"
                    v-model="useUserData.email"
                    type="email"
                    trim
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PHONE')"
                  label-for="phone"
                >
                  <b-form-input
                    id="phone"
                    v-model="useUserData.phone"
                    trim
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <hr>
            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>
              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  :rules="useUserData.address.road_name || useUserData.address.zip_code || useUserData.address.city_id || useUserData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="useUserData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  :rules="useUserData.address.house_number || useUserData.address.zip_code || useUserData.address.city_id || useUserData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="useUserData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="useUserData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  :rules="useUserData.address.road_name || useUserData.address.house_number || useUserData.address.city_id || useUserData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <v-select
                      v-model="useUserData.address.zip_code"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="zipCodes"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="zip_code"
                      @input="getCityId($event)"
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  :rules="useUserData.address.road_name || useUserData.address.house_number || useUserData.address.zip_code || useUserData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    id="city_id"
                    :label="$t('DEBTIA_CITY')"
                    class="city"
                    label-for="city_id"
                  >
                    <v-select
                      v-model="useUserData.address.city_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="cities"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="city_id"
                      @input="getZipCode($event)"
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Country"
                  :rules="useUserData.address.road_name || useUserData.address.house_number || useUserData.address.zip_code || useUserData.address.city_id ? 'required' : ''"
                >
                  <b-form-group
                    id="country_id"
                    :label="$t('DEBTIA_COUNTRY')"
                    class="country"
                    label-for="country_id"
                  >
                    <v-select
                      v-model="useUserData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="contact_address_country"
                      :value="'Select Country'"
                    >
                      <template
                        #selected-option-container="{ option }"
                      >
                        <div class="vs__selected">
                          {{ $t(option.label) }}</div>
                      </template>
                      <template
                        slot="option"
                        slot-scope="option"
                      >
                        {{ $t(option.label) }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
            <hr>
            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              Save Changes
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              Reset
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BCard,
  BFormInvalidFeedback, BOverlay, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import countries from '@/countries'
import formValidation from '@core/comp-functions/forms/form-validation'
import { required, alphaNum, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import contactStoreModule from '../contactStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      countries,
      required,
      alphaNum,
      email,
      alertErrors: [],
      useUserData: { ...this.userData },
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
      maxCharacters: 50,
      maxCharactersAllowed: 50,
      zipCodes: [],
      cities: [],
    }
  },
  setup(props) {
    const loader = false
    const refInputEl = ref(null)
    const previewEl = ref(null)
    const {
      refFormObserver,
      getValidationState,
    } = formValidation(props.userData)
    return {
      avatarText,
      loader,
      refInputEl,
      previewEl,
      refFormObserver,
      getValidationState,
      BFormInvalidFeedback,
    }
  },
  created() {
    this.getZipCodesDropdown()
    this.getCitiesDropdown()
  },
  methods: {
    async getZipCodesDropdown() {
      await axios.get('settings/zip-codes/dropdown?country=Danmark')
        .then(response => {
          this.zipCodes = response.data.data
        }).catch(() => false)
    },
    async getCitiesDropdown() {
      await axios.get('settings/get-cities-dropdown?country=Danmark')
        .then(response => {
          this.cities = response.data.data
        }).catch(error => {
          console.log(error)
        })
    },
    async getCityId(event) {
      await axios.get(`settings/get-city-id-by-zip-code?code=${event}`)
        .then(response => {
          if (response.data.data.city_name !== null) {
            this.useUserData.address.city_id = response.data.data.city_id
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async getZipCode(event) {
      await axios.get(`settings/get-zip-code-by-city-id?city_id=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.useUserData.address.zip_code = response.data.data.zip_code
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async onSubmit() {
      this.loader = true

      const data = { ...this.useUserData }
      const USER_APP_STORE_MODULE_NAME = 'app-contact'
      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, contactStoreModule)
      data.debt_case_id = this.debtCaseId
      // console.log('--->', data)
      store.dispatch('app-contact/editContact', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace('/contacts/list')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Client Updated',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Contact successfully Updated!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = []
          this.alertErrors.push(...error.data.message)
          // console.log('error**', this.alertErrors)
          this.loader = false
        })
      return true
    },

    resetForm() {
      this.useUserData = { ...this.userData }
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}
</style>
